import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getFlashbookDesigns } from '../../services/flashbooks';

const PAGE_LIMIT = 24;

function useGetFlashbookDesigns({
  flashbookId,
  isArchived = false,
  enabled: passedEnabled = true,
}: {
  flashbookId: string | undefined;
  isArchived?: boolean;
  enabled?: boolean;
}) {
  const enabled = !!flashbookId && passedEnabled;

  const queryKey = useMemo(
    () => ['flashbook-designs', { flashbookId, isArchived }],
    [flashbookId, isArchived],
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey,
    queryFn: async ({ pageParam: cursor }) =>
      getFlashbookDesigns({
        flashbookId: flashbookId!,
        isArchived,
        cursor,
        limit: PAGE_LIMIT,
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: lastPage => {
      if (!lastPage.meta?.hasMore || !lastPage.meta?.cursor) return undefined;
      return lastPage.meta.cursor;
    },
    enabled,
  });

  const designs = useMemo(
    () => data?.pages?.flatMap(page => page.result) ?? [],
    [data?.pages],
  );

  return {
    queryKey,
    data,
    designs,
    isLoading: enabled && isLoading,
    isFetching,
    isFetchingNextPage,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
  };
}

export default useGetFlashbookDesigns;
